import React, { createContext, useContext, useState, useEffect } from 'react';
import { Snackbar, Alert, Slide, useMediaQuery } from '@mui/material';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Grace period before a contribution counts as late (system_settings.latePenaltyDays)
const PAYMENT_DUE_DAY = 5;

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

function SlideUpTransition(props) {
  return <Slide {...props} direction="up" />;
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const isPhone = useMediaQuery('(max-width:599.95px)', { noSsr: true });
  const [snackbars, setSnackbars] = useState([]);

  const removeNotification = (id) => {
    setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
  };

  const showNotification = ({ message, severity = 'info', duration = 4000, persistent = false }) => {
    const id = Date.now();
    
    const snackbar = {
      id,
      message,
      severity,
      duration: persistent ? null : duration,
      open: true,
    };
    
    setSnackbars(prev => [...prev, snackbar]);
    
    // Also show toast for important notifications. On a phone the alert below
    // already says the same thing, so a second banner would just stack on it.
    if (!isPhone) {
      if (severity === 'error') {
        toast.error(message, { duration });
      } else if (severity === 'warning') {
        toast.error(message, { duration }); // react-hot-toast doesn't have warning, use error for warnings
      }
    }
    
    // Auto-remove non-persistent notifications
    if (!persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  };

  const getNextMeetingDate = () => {
    const now = dayjs();
    let firstFriday = now.startOf('month');
    
    // Find first Friday
    while (firstFriday.day() !== 5) {
      firstFriday = firstFriday.add(1, 'day');
    }
    
    // If first Friday has passed, get next month's first Friday
    if (firstFriday.isBefore(now)) {
      const firstOfNextMonth = now.add(1, 'month').startOf('month');
      firstFriday = firstOfNextMonth;
      while (firstFriday.day() !== 5) {
        firstFriday = firstFriday.add(1, 'day');
      }
    }
    
    return firstFriday.hour(22).minute(0).second(0);
  };

  const checkPaymentDeadlines = async () => {
    const currentDate = dayjs();
    const currentMonth = currentDate.format('YYYY-MM');

    // The system admin account does not contribute, so it is never late.
    if (user?.isSystemAdmin) {
      return;
    }

    // Only warn about a late payment if this member actually owes one. This used
    // to fire purely off the calendar, so anyone who had already paid - or whose
    // records were backfilled - still got "late penalty has been applied".
    let hasOutstandingPayment = false;
    try {
      const response = await axios.get(`/api/payments/user/${user.id}`);
      const thisMonth = response.data.find(p => p.payment_month === currentMonth);
      hasOutstandingPayment = !thisMonth || thisMonth.status !== 'completed';
    } catch (error) {
      // Can't confirm the payment state - stay quiet rather than cry wolf
      hasOutstandingPayment = false;
    }

    if (hasOutstandingPayment && currentDate.date() > PAYMENT_DUE_DAY) {
      const daysLate = currentDate.date() - PAYMENT_DUE_DAY;

      // Show escalating notifications
      if (daysLate >= 7) {
        showNotification({
          message: `💸 Payment is ${daysLate} days overdue. A late penalty may have been applied.`,
          severity: 'error',
          duration: 10000,
          persistent: true,
        });
      } else if (daysLate >= 5) {
        showNotification({
          message: '🚨 Payment is 5 days overdue! Late penalty may apply.',
          severity: 'error',
          duration: 10000,
        });
      } else {
        showNotification({
          message: '⚠️ Payment deadline passed! Please make your monthly payment.',
          severity: 'warning',
          duration: 8000,
        });
      }
    }

    // Reminder before deadline
    if (hasOutstandingPayment && currentDate.date() === PAYMENT_DUE_DAY - 2) {
      showNotification({
        message: `📅 Payment deadline is in 2 days (${PAYMENT_DUE_DAY}th of month)`,
        severity: 'info',
        duration: 6000,
      });
    }

    // Meeting reminder (First Friday of month at 10 PM)
    const nextMeeting = getNextMeetingDate();
    const hoursToMeeting = nextMeeting.diff(currentDate, 'hour');
    
    // Most urgent case first - the <= 2 branch was unreachable behind <= 24
    if (hoursToMeeting > 0 && hoursToMeeting <= 2) {
      showNotification({
        message: `🔔 Meeting starts in ${hoursToMeeting} hour${hoursToMeeting === 1 ? '' : 's'}!`,
        severity: 'warning',
        duration: 10000,
        persistent: true,
      });
    } else if (hoursToMeeting > 0 && hoursToMeeting <= 24) {
      showNotification({
        message: `📢 Meeting tomorrow at 10 PM! Mark your calendar.`,
        severity: 'info',
        duration: 8000,
      });
    }
  };

  useEffect(() => {
    if (user) {
      checkPaymentDeadlines();
      
      // Set up periodic checks
      const interval = setInterval(checkPaymentDeadlines, 60 * 60 * 1000); // Check every hour
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCloseSnackbar = (id) => (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    removeNotification(id);
  };

  const value = {
    showNotification,
    removeNotification,
    snackbars,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open={snackbar.open}
          autoHideDuration={snackbar.duration}
          onClose={handleCloseSnackbar(snackbar.id)}
          TransitionComponent={isPhone ? SlideUpTransition : SlideTransition}
          anchorOrigin={{ vertical: isPhone ? 'bottom' : 'top', horizontal: 'center' }}
          sx={{
            position: 'fixed',
            // Stack downwards by position in the queue. This used to be
            // `snackbar.id * 5`, and since the id is Date.now() every alert after
            // the first was positioned billions of pixels off-screen.
            // On a phone the top edge is the app bar (its bell and avatar must
            // stay tappable), so alerts stack upwards from just above the
            // floating collection button instead.
            ...(isPhone
              ? { top: 'auto', bottom: 88 + index * 72, left: 16, right: 16, transform: 'none' }
              : { top: 24 + index * 72 }),
            // Phone: below modals (1300) so a full-screen form is not covered by
            // a reminder sitting over its fields
            zIndex: isPhone ? 1250 : 2000,
          }}
        >
          <Alert
            onClose={handleCloseSnackbar(snackbar.id)}
            severity={snackbar.severity}
            sx={{ 
              width: '100%',
              boxShadow: 3,
              fontWeight: 500,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};